import {
  KEYCODE_MAP,
  KEYCODE_MACRO_MAP,
} from "@eweadn-qiankun/shared-components";
import type { KeyConfig } from "@eweadn-qiankun/shared-components";
import { getCbTriggerSlotDisplayLabel } from "./Cb";
import {
  applyDksTriggerDataFromPayload,
  isTank68HeDksCodec,
} from "./dksTriggerDataUtils";

type AdvancedKeyCatalogItem = Record<string, unknown>;
type LayoutMatrix = KeyConfig[][];

function findLayoutKey(
  layout: LayoutMatrix | undefined,
  row: number,
  col: number
): KeyConfig | undefined {
  if (!layout?.length) return undefined;
  for (const layoutRow of layout) {
    for (const key of layoutRow) {
      if (key.originalRow === row && key.originalCol === col) {
        return key;
      }
    }
  }
  return undefined;
}

function resolveItemPayload(item: Record<string, unknown>) {
  const data = item.data as Record<string, unknown> | undefined;
  if (data?.data && typeof data.data === "object") {
    return data.data as Record<string, unknown>;
  }
  return data ?? {};
}

function keyCodeLabel(kc: number): string {
  return KEYCODE_MAP[kc]?.name || KEYCODE_MACRO_MAP[kc]?.name || String(kc);
}

/** 从已配置列表项还原 SettingAfter 所需的 checkData（对齐 Home.tsx getUpdate） */
export function buildAdvancedKeyCheckDataFromListItem(
  item: Record<string, unknown>,
  layout: LayoutMatrix | undefined,
  catalog: AdvancedKeyCatalogItem[],
  options?: { keyboardType?: string | null }
): AdvancedKeyCatalogItem | null {
  const mode = Number(item.mode ?? 0);
  if (!mode) return null;

  const template = catalog.find((entry) => entry.value === mode);
  if (!template) return null;

  const newCheckData = JSON.parse(JSON.stringify(template)) as Record<
    string,
    unknown
  >;
  const payload = resolveItemPayload(item);
  const originalRow = Number(item.originalRow ?? item.primaryRow ?? 0);
  const originalCol = Number(item.originalCol ?? item.primaryCol ?? 0);
  const primaryCell =
    findLayoutKey(layout, originalRow, originalCol) ??
    ({
      code: item.code,
      uuid: item.uuid,
      label: item.label,
      originalRow,
      originalCol,
    } as KeyConfig);

  const selectPrimary = {
    code: primaryCell.code,
    uuid: primaryCell.uuid,
    col: primaryCell.originalCol,
    label: primaryCell.label ?? item.label,
    row: primaryCell.originalRow,
    keyCode: primaryCell.code ?? item.keyCode,
  };

  if (mode === 5) {
    newCheckData.selectData = [selectPrimary];
    const kcs = (payload.kcs as number[]) || [];
    newCheckData.otherData = {
      mode,
      kcs,
      delay: Number(payload.delay ?? 0),
    };
    newCheckData.triggerData = kcs.map((kc) => ({
      uuid: null,
      col: null,
      label: keyCodeLabel(kc),
      row: null,
      keyCode: kc,
      check: false,
    }));
    return newCheckData;
  }

  if (mode === 4) {
    newCheckData.selectData = [selectPrimary];
    const kc = Number(payload.kcs);
    newCheckData.otherData = { mode, kcs: kc, delay: 1 };
    newCheckData.triggerData = [
      {
        uuid: null,
        col: null,
        label: keyCodeLabel(kc),
        row: null,
        keyCode: kc,
        check: false,
      },
    ];
    return newCheckData;
  }

  if (mode === 3) {
    newCheckData.selectData = [selectPrimary];
    const kcs = (payload.kcs as number[]) || [];
    newCheckData.otherData = {
      mode,
      kcs,
      time: payload.time,
    };
    newCheckData.triggerData = kcs.map((kc) => ({
      uuid: null,
      col: null,
      label: keyCodeLabel(kc),
      row: null,
      keyCode: kc,
      check: false,
    }));
    return newCheckData;
  }

  if (mode === 2) {
    newCheckData.selectData = [selectPrimary];
    const kcs = (payload.kcs as number[]) || [];
    const dbs = (payload.dbs as number[]) || [];
    newCheckData.otherData = { mode, kcs, dbs };
    const triggerData = kcs.map((kc, index) => ({
      uuid: null,
      col: null,
      label: keyCodeLabel(kc),
      row: null,
      keyCode: kc,
      check: false,
      value: Number(dbs[index] ?? 0) / 1000,
    }));
    const triggerDataObj = {
      code: "",
      uuid: "",
      col: "",
      label: "",
      row: "",
      keyCode: "",
      check: false,
      value: 0.01,
    };
    if (triggerData.length === 1) {
      triggerData.push({ ...triggerDataObj }, { ...triggerDataObj });
    } else if (triggerData.length === 2) {
      triggerData.push({ ...triggerDataObj });
    }
    newCheckData.triggerData = triggerData;
    return newCheckData;
  }

  if (mode === 1) {
    newCheckData.selectData = [selectPrimary];
    const kcs = (payload.kcs as number[]) || [];
    const trps = (payload.trps as number[]) || [];
    const dbs = payload.dbs;
    newCheckData.otherData = { mode, trps, dbs };
    const useTank68HeDksCodec = isTank68HeDksCodec(options?.keyboardType);
    newCheckData.triggerData = applyDksTriggerDataFromPayload(
      (newCheckData.triggerData as Array<{ binaryData?: unknown[] }>) ?? [],
      kcs,
      trps,
      useTank68HeDksCodec
    );
    return newCheckData;
  }

  if (mode === 6 || mode === 7 || mode === 9 || mode === 10) {
    const partnerRow = Number(payload.row2 ?? item.partnerRow);
    const partnerCol = Number(payload.col2 ?? item.partnerCol);
    const partnerCell = findLayoutKey(layout, partnerRow, partnerCol);
    newCheckData.selectData = [
      selectPrimary,
      {
        code: partnerCell?.code,
        uuid: partnerCell?.uuid,
        col: partnerCell?.originalCol,
        label: partnerCell?.label ?? item.label2,
        row: partnerCell?.originalRow,
        keyCode: partnerCell?.code,
      },
    ];
    const kcs = (payload.kcs as number[]) || [];
    if (mode === 6) {
      newCheckData.otherData = {
        mode: item.socdMode ?? payload.socdMode,
        kcs,
        delay: payload.delay ?? 0,
      };
    } else {
      newCheckData.otherData = {
        mode,
        kcs,
        delay: payload.delay ?? 0,
      };
    }
    newCheckData.triggerData = kcs.map((kc) => ({
      uuid: null,
      col: null,
      label: keyCodeLabel(kc),
      row: null,
      keyCode: kc,
      check: false,
    }));
    return newCheckData;
  }

  if (mode === 8) {
    newCheckData.selectData = [selectPrimary];
    const kcs = (payload.kcs as number[]) || [];
    newCheckData.otherData = { kcs };
    newCheckData.triggerData = kcs.map((kc, index) => ({
      uuid: null,
      col: null,
      label: getCbTriggerSlotDisplayLabel(kc, index),
      row: null,
      keyCode: kc,
      check: false,
    }));
    return newCheckData;
  }

  return null;
}
